'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { OrganizerReview } from '@/lib/api';
import { getOrigin } from '@/lib/api';

export interface TestimonialItem {
  text: string;
  image: string | null;
  name: string;
  role: string;
}

function mapReviewToItem(r: OrganizerReview): TestimonialItem {
  const image =
    r.organizer_avatar_url && r.organizer_avatar_url.startsWith('http')
      ? r.organizer_avatar_url
      : r.organizer_avatar_url
        ? `${getOrigin()}${r.organizer_avatar_url}`
        : null;
  const role = [r.event_name, r.organizer_company].filter(Boolean).join(' · ') || 'Organizer';
  return {
    text: r.review_text,
    image,
    name: r.organizer_name,
    role,
  };
}

const TestimonialsColumn = (props: {
  className?: string;
  testimonials: TestimonialItem[];
  duration?: number;
}) => {
  return (
    <div className={props.className}>
      <motion.ul
        animate={{ translateY: '-50%' }}
        transition={{
          duration: props.duration ?? 10,
          repeat: Infinity,
          ease: 'linear',
          repeatType: 'loop',
        }}
        className="flex flex-col gap-6 pb-6 bg-transparent list-none m-0 p-0"
      >
        {[
          ...new Array(4).fill(0).map((_, index) => (
            <React.Fragment key={index}>
              {props.testimonials.map(({ text, image, name, role }, i) => (
                <motion.li
                  key={`${index}-${i}`}
                  aria-hidden={index === 1}
                  tabIndex={index === 1 ? -1 : 0}
                  whileHover={{
                    scale: 1.03,
                    y: -8,
                    boxShadow:
                      '0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    transition: { type: 'spring', stiffness: 400, damping: 17 },
                  }}
                  whileFocus={{
                    scale: 1.03,
                    y: -8,
                    boxShadow:
                      '0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    transition: { type: 'spring', stiffness: 400, damping: 17 },
                  }}
                  className={cn(
                    'p-7 rounded-3xl border border-[#8D9F92]/40',
                    'shadow-xs max-w-xs w-full bg-[#8D9F92]/20 backdrop-blur-md',
                    'cursor-default select-none group focus:outline-none focus:ring-2 focus:ring-[#8D9F92] transition-all duration-200 hover:border-[#8D9F92]/70 hover:bg-[#8D9F92]/30'
                  )}
                >
                  <blockquote className="m-0 p-0">
                    <p className="text-dark font-normal leading-relaxed m-0 text-sm sm:text-base">
                      &ldquo;{text}&rdquo;
                    </p>
                    <footer className="flex items-center gap-3 mt-6">
                      {image ? (
                        <Image
                          width={40}
                          height={40}
                          src={image}
                          alt={`Avatar of ${name}`}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-[#8D9F92]/40 group-hover:ring-[#8D9F92]/70 transition-all"
                        />
                      ) : (
                        <span className="h-10 w-10 rounded-full bg-[#8D9F92]/30 flex items-center justify-center text-sm font-semibold text-dark ring-1 ring-[#8D9F92]/50">
                          {(name || 'O').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="flex flex-col">
                        <cite className="font-semibold not-italic tracking-tight leading-5 text-dark text-sm">
                          {name}
                        </cite>
                        <span className="text-xs leading-5 tracking-tight text-dark/75 font-normal">
                          {role}
                        </span>
                      </div>
                    </footer>
                  </blockquote>
                </motion.li>
              ))}
            </React.Fragment>
          )),
        ]}
      </motion.ul>
    </div>
  );
};

export const DEFAULT_TESTIMONIALS: OrganizerReview[] = [
  {
    id: '1',
    organizer_name: 'Sarah Chen',
    organizer_company: 'TechConf Global',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'EnthiosHub helped us manage our annual tech conference with 2,000+ attendees effortlessly. The QR-based ticket scanning and live attendance stats saved us hours.',
    event_name: 'DevSummit 2026',
    featured: true,
    created_at: '2026-03-15',
  },
  {
    id: '2',
    organizer_name: 'Marcus Johnson',
    organizer_company: 'Startup Velocity',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'From tiered ticketing to real-time revenue analytics, everything just worked out of the box. Easily the most reliable organizer platform in India.',
    event_name: 'Founders Meetup',
    featured: true,
    created_at: '2026-03-20',
  },
  {
    id: '3',
    organizer_name: 'Elena Rodriguez',
    organizer_company: 'Creative Leaders Network',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'The attendee management and bulk communications tools saved our volunteer crew countless hours of stress during festival week.',
    event_name: 'Design & Culture Fest',
    featured: true,
    created_at: '2026-03-25',
  },
  {
    id: '4',
    organizer_name: 'James Wilson',
    organizer_company: 'Campus Community',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'We hosted our university festival entirely on EnthiosHub. Over 4,500 passes scanned with zero bottleneck at the entry gates.',
    event_name: 'Spring College Summit',
    featured: false,
    created_at: '2026-04-01',
  },
  {
    id: '5',
    organizer_name: 'Priya Sharma',
    organizer_company: 'Craft & Code Masterclasses',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'The multi-session schedules and pass configurations were exactly what our workshop series needed. Attendee feedback has been incredible.',
    event_name: 'UI/UX Masterclass',
    featured: true,
    created_at: '2026-04-05',
  },
  {
    id: '6',
    organizer_name: 'Alex Rivera',
    organizer_company: 'Indie Hackers Circle',
    organizer_avatar_url: null,
    rating: 5,
    review_text: 'Instant payouts, transparent platform fees, and a beautiful landing page that converts visitors seamlessly. We will definitely use it again.',
    event_name: 'AI Builders Demo Day',
    featured: true,
    created_at: '2026-04-10',
  },
];

export interface TestimonialV2Props {
  /** Dynamic organizer reviews from API; if empty, section displays curated default testimonials */
  testimonials?: OrganizerReview[];
  /** Section title */
  heading?: string;
  /** Subtitle / description */
  description?: string;
  /** Optional badge label above heading */
  badge?: string;
  className?: string;
}

export function TestimonialV2({
  testimonials,
  heading = 'What Organizers Say',
  description = 'Discover how event organizers use EnthiosHub to run smooth events.',
  badge = 'Testimonials',
  className,
}: TestimonialV2Props) {
  const sourceReviews =
    Array.isArray(testimonials) && testimonials.length > 0
      ? testimonials
      : DEFAULT_TESTIMONIALS;

  const items = sourceReviews.map(mapReviewToItem);

  // Distribute items into 3 columns for the masonry effect
  const firstColumn = items.slice(0, Math.ceil(items.length / 3));
  const secondColumn = items.slice(Math.ceil(items.length / 3), Math.ceil((2 * items.length) / 3));
  const thirdColumn = items.slice(Math.ceil((2 * items.length) / 3));

  // Dynamic duration based on content length to maintain consistent speed
  const baseDuration = (len: number) => Math.max(10, len * 2);

  return (
    <section
      aria-labelledby="testimonials-heading"
      className={cn('bg-transparent py-24 relative overflow-hidden', className)}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{
          duration: 1.2,
          ease: [0.16, 1, 0.3, 1],
          opacity: { duration: 0.8 },
        }}
        className="container px-4 z-10 mx-auto"
      >
        <div className="flex flex-col items-center justify-center max-w-[640px] mx-auto mb-16 px-4">
          <div className="flex justify-center">
            <div className="border border-[#8D9F92]/60 py-1 px-4 rounded-full text-xs font-semibold tracking-wide uppercase text-[#2D3536] bg-[#8D9F92]/20">
              {badge}
            </div>
          </div>
          <h2
            id="testimonials-heading"
            className="text-4xl md:text-5xl font-bold tracking-tight mt-6 text-center text-[#2D3536] font-display"
          >
            {heading}
          </h2>
          <p className="text-center mt-5 text-[#2D3536]/85 font-normal text-lg leading-relaxed max-w-sm sm:max-w-md">
            {description}
          </p>
        </div>

        <div
          className="flex justify-center gap-6 mt-10 [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)] max-h-[840px] overflow-hidden"
          role="region"
          aria-label="Scrolling Testimonials"
        >
          <TestimonialsColumn testimonials={firstColumn} duration={baseDuration(firstColumn.length)} />
          <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={baseDuration(secondColumn.length) + 2} />
          <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={baseDuration(thirdColumn.length) + 1} />
        </div>
      </motion.div>
    </section>
  );
}

export default TestimonialV2;
